import axios from "axios";
import { Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { userError, userSuccess } from "../../../utils/toastUtils";

const TestimonialModal = ({
    onClose,
    editingTestimonial,
    fetchTestimonials,
}) => {
    const API = process.env.REACT_APP_API_URL;

    const [form, setForm] = useState({
        name: "",
        text: "",
        rating: 5,
        sequence: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const inputClass = (field) =>
        `w-full mt-1 rounded-lg px-4 py-3 outline-none border transition
    ${errors[field]
            ? "border-red-400 focus:border-red-500"
            : "border-gray-300 focus:border-[#f0653a]"
        }`;

    // Prefill on edit
    useEffect(() => {
        if (editingTestimonial) {
            setForm({
                name: editingTestimonial.name || "",
                text: editingTestimonial.text || "",
                rating: editingTestimonial.rating || 5,
                sequence: editingTestimonial.sequence || 0,
            });
        }
    }, [editingTestimonial]);

    // Validation
    const validate = () => {
        const newErrors = {};

        if (!form.name.trim()) newErrors.name = "Name is required";
        if (!form.text.trim()) newErrors.text = "Testimonial text is required";

        if (form.rating < 1 || form.rating > 5) {
            newErrors.rating = "Rating must be between 1 and 5";
        }

        if (form.sequence < 0) {
            newErrors.sequence = "Sequence cannot be negative";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Save
    const handleSave = async () => {
        if (!validate()) return;

        try {
            setLoading(true);

            if (editingTestimonial) {
                await axios.put(
                    `${API}/testimonials/${editingTestimonial._id}`,
                    form
                );
                userSuccess("Testimonial updated");
            } else {
                await axios.post(`${API}/testimonials`, form);
                userSuccess("Testimonial created");
            }

            fetchTestimonials();
            onClose();
        } catch (err) {
            userError("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div className="bg-white w-full max-w-xl h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">

                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {editingTestimonial ? "Edit Testimonial" : "Create Testimonial"}
                    </h2>

                    <button onClick={onClose} className="text-gray-500 hover:text-black">
                        <X />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

                    {/* Name */}
                    <div>
                        <label className="text-sm font-medium text-gray-700">
                            Customer Name
                        </label>
                        <input
                            type="text"
                            placeholder="e.g. Riya Sharma"
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                            className={inputClass("name")}
                        />
                        {errors.name && (
                            <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                        )}
                    </div>

                    {/* Text */}
                    <div>
                        <label className="text-sm font-medium text-gray-700">
                            Testimonial
                        </label>
                        <textarea
                            rows="4"
                            placeholder="Write the testimonial..."
                            value={form.text}
                            onChange={(e) =>
                                setForm({ ...form, text: e.target.value })
                            }
                            className={inputClass("text")}
                        />
                        {errors.text && (
                            <p className="text-xs text-red-500 mt-1">{errors.text}</p>
                        )}
                    </div>

                    {/* Rating + Sequence */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium text-gray-700">
                                Rating (1–5)
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={5}
                                value={form.rating}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setForm({
                                        ...form,
                                        rating: value === "" ? "" : Number(value),
                                    });
                                }}

                                step={0.1}
                                className={inputClass("rating")}
                            />
                            {errors.rating && (
                                <p className="text-xs text-red-500 mt-1">
                                    {errors.rating}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-700">
                                Sequence
                            </label>
                            <input
                                type="number"
                                placeholder="0"
                                value={form.sequence}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setForm({
                                        ...form,
                                        sequence: value === "" ? "" : Number(value),
                                    });
                                }}
                                className={inputClass("sequence")}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Lower number appears first
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t bg-white sticky bottom-0">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 rounded-md border text-gray-600 hover:bg-gray-100"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="px-5 py-2 rounded-md bg-[#f0653a] text-white font-semibold hover:bg-[#d6532d] flex items-center gap-2"
                    >
                        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                        {editingTestimonial ? "Save Changes" : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TestimonialModal;